/**
 * Formatação pt-BR usada pela web (telas) e pela API (relatórios). Um único lugar para moeda,
 * números, percentuais, datas e unidades de medida dos KPIs.
 * Observação: o Intl usa espaço não separável (U+00A0) entre "R$" e o valor.
 */

const LOCALE = 'pt-BR';
const TIME_ZONE = 'America/Sao_Paulo';

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Texto padrão para valor ausente. */
export const EMPTY_VALUE = '—';

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Número com no máximo `maxDecimals` casas, sem zeros à direita (12,5 / 12 / 12,75). */
export function formatDecimal(value: number | null | undefined, maxDecimals = 2): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: maxDecimals }).format(value);
}

export function formatCurrency(value: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'BRL',
    ...(opts.compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
  }).format(value);
}

/** Número curto para cards e gráficos: 2,8 mi · 12 mil. */
export function formatCompact(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  return new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * Percentual a partir de um valor já em pontos percentuais: 90.67 → "90,67%".
 * (Performance no Fluxus BSC é sempre guardada em pontos percentuais, como no PRD §14.)
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  return `${formatNumber(value, decimals)}%`;
}

/** Variação com sinal explícito: +4,7% · −2,0%. Usa o sinal de menos tipográfico. */
export function formatSignedPercent(value: number | null | undefined, decimals = 1): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  const abs = formatNumber(Math.abs(value), decimals);
  if (value > 0) return `+${abs}%`;
  if (value < 0) return `−${abs}%`;
  return `${abs}%`;
}

type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  // Datas puras (AAAA-MM-DD) são dias de calendário: não podem "voltar um dia" por causa do fuso.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d, 12));
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: DateInput): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(value: DateInput): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}

/** Mês de referência: "jan/2026". */
export function formatMonth(value: DateInput): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const month = new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, month: 'short' })
    .format(date)
    .replace('.', '');
  const year = new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, year: 'numeric' }).format(date);
  return `${month}/${year}`;
}

/** Tempo relativo curto: "agora", "há 5 min", "há 2 h", "há 3 dias", depois a data. */
export function formatRelative(value: DateInput, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (seconds < 0) return formatDateTime(date);
  if (seconds < 60) return 'agora';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  return formatDate(date);
}

/** Unidades de medida de KPI do PRD §11.11. `custom` usa o rótulo informado pela empresa. */
export type MeasureUnit =
  | 'BRL'
  | 'PERCENT'
  | 'UNITS'
  | 'TONS'
  | 'LITERS'
  | 'HOURS'
  | 'DAYS'
  | 'CLIENTS'
  | 'TICKETS'
  | 'AVAILABILITY'
  | 'INDEX'
  | 'SCORE'
  | 'CUSTOM';

export const MEASURE_UNIT_LABELS: Record<MeasureUnit, string> = {
  BRL: 'R$',
  PERCENT: '%',
  UNITS: 'unidades',
  TONS: 'toneladas',
  LITERS: 'litros',
  HOURS: 'horas',
  DAYS: 'dias',
  CLIENTS: 'clientes',
  TICKETS: 'chamados',
  AVAILABILITY: 'disponibilidade',
  INDEX: 'índice',
  SCORE: 'nota',
  CUSTOM: 'unidade customizada',
};

const SUFFIX: Partial<Record<MeasureUnit, string>> = {
  UNITS: 'un.',
  TONS: 't',
  LITERS: 'L',
  HOURS: 'h',
  DAYS: 'dias',
  CLIENTS: 'clientes',
  TICKETS: 'chamados',
};

/** Valor de KPI com a unidade: R$ 2.720.000,00 · 93,5% · 120 t · 4,5 (nota). */
export function formatMeasure(
  value: number | null | undefined,
  unit: MeasureUnit,
  opts: { compact?: boolean; customLabel?: string; decimals?: number } = {},
): string {
  if (!isFiniteNumber(value)) return EMPTY_VALUE;
  if (unit === 'BRL') return formatCurrency(value, { compact: opts.compact });
  if (unit === 'PERCENT' || unit === 'AVAILABILITY') return formatPercent(value, opts.decimals ?? 1);
  const number = opts.compact ? formatCompact(value) : formatDecimal(value, opts.decimals ?? 2);
  if (unit === 'INDEX' || unit === 'SCORE') return number;
  if (unit === 'CUSTOM') return opts.customLabel ? `${number} ${opts.customLabel}` : number;
  return `${number} ${SUFFIX[unit] ?? ''}`.trim();
}
